<template>
    <v-card-text class="narrow-container">
        <v-form @keyup.native.enter="onSubmit">
            <v-text-field
                v-model="form.email"
                :error-messages="form.$errors.email"
                label="Enter your email adress"
                type="email"
                outlined
                required
                @blur="form.validate('email')"
                @input="delete form.$errors.email"
            />

            <v-text-field
                v-model="form.name"
                :error-messages="form.$errors.name"
                label="Full name"
                outlined
                required
                @blur="form.validate('name')"
                @input="delete form.$errors.name"
            />

            <v-tooltip v-model="showPasswordTooltip" content-class="password-tooltip" right>
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="form.password"
                        :error-messages="form.$errors.password"
                        label="Password"
                        type="password"
                        outlined
                        required
                        v-bind="attrs"
                        v-on="on"
                        @blur="
								form.validate('password');
								showPasswordTooltip = false;
							"
                        @input="delete form.$errors.password"
                        @focus="showPasswordTooltip = true"
                    />
                </template>
                <div class="pa-5">
                    <span class="d-block frontpage-text mb-4"
                          :class="{ 'text-decoration-line-through': passValidation.uppercase }"
                    >
                        One uppercase letter
                    </span>
                    <span class="d-block frontpage-text mb-4"
                          :class="{ 'text-decoration-line-through': passValidation.lowercase }"
                    >
                        One lowercase letter
                    </span>
                    <span class="d-block frontpage-text mb-4"
                          :class="{ 'text-decoration-line-through': passValidation.number }"
                    >
                        One number
                    </span>
                    <span class="d-block frontpage-text mb-4"
                          :class="{ 'text-decoration-line-through': passValidation.specialChar }"
                    >
                        One special character
                    </span>
                    <span class="d-block frontpage-text"
                          :class="{ 'text-decoration-line-through': passValidation.length }"
                    >8+ characters
                    </span>
                </div>
            </v-tooltip>

            <v-select
                v-model="form.plan_id"
                :items="getSelectablePlansWhenRegistering"
                item-text="name"
                item-value="id"
                :menu-props="{ offsetY: true }"
                :error-messages="form.$errors.plan_id"
                placeholder="Choose your plan"
                outlined
                @blur="form.validate('plan_id')"
                @input="delete form.$errors.plan_id"
            />

            <div class="d-flex align-center">
                <v-checkbox dense v-model="termsOfServiceAccepted" :ripple="false"></v-checkbox>
                <span class="frontpage-text d-block" style="line-height: 1.2">
                        By clicking on Sign Up, you are confirming that you have read and agree to fylm.ai <a
                        href="https://fylm.ai/terms-of-service" target="_blank"
                        class="frontpage-link text-decoration-underline"
                >Terms of Service</a> and <a href="https://fylm.ai/privacy-policy/" target="_blank"
                                             class="frontpage-link text-decoration-underline"
                >Privacy Policy.</a>
                    </span>
            </div>

            <div id="cf-turnstile" class="d-flex justify-center mb-5"></div>

            <v-btn color="colord-button primary" :disabled="!termsOfServiceAccepted" :loading="form.$busy" large depressed block rounded
                   @click="onSubmit" class="mb-4"
            >
                Sign Up
            </v-btn>

            <span class="frontpage-text mt-6 d-block text-center">
                You already have an account?
                <router-link :to="{ name: 'login' }" class="frontpage-link text-decoration-underline">
                    Sign In.
                </router-link>
            </span>
        </v-form>
    </v-card-text>
</template>

<script>
import * as rules from "vuelidate/lib/validators";
import { mapActions, mapGetters, mapState } from "vuex";
import cloudflareConfig from '@/assets/js/config/CloudFlare';

export default {
    name: "Register",

    async mounted() {
        await this.$loadScript(cloudflareConfig.turnstile.scriptURL);
        this.cf_turnstile_widget_id = window.turnstile.render('#cf-turnstile', {
            ...cloudflareConfig.turnstile,
            action: 'Register',
            callback: token => {
                this.form.cf_turnstile_response = token
            },
        })
    },

    async created() {
        await this.$store.dispatch('Plans/fetchPlans');
        await this.$store.dispatch("Products/fetch");

        if (this.$route.params.product) {
            if (this.$route.params.product == 'lite') {
                this.form.plan_id = this.PLAN_LITE.id;
            } else if (this.products.find(product => product.fs_slug == this.$route.params.product)) {
                localStorage.setItem('selected_product', this.$route.params.product);
                this.form.plan_id = this.PLAN_LITE.id;
            }
        }
    },

    data() {
        return {
            showPasswordTooltip: false,
            cf_turnstile_widget_id: null,
            termsOfServiceAccepted: false,

            form: new Form(this, "form", {
                name: null,
                plan_id: 7,
                email: null,
                password: null,
                cf_turnstile_response: null,
            }),
        };
    },
    validations: {
        form: {
            name: {
                required: rules.required,
                minLength: rules.minLength(4),
                fullName: rules.helpers.regex("fullName", /^[a-z ,.'-]+$/i),
            },
            email: {required: rules.required, email: rules.email},
            password: {
                required: rules.required,
                minLength: rules.minLength(8),
                maxLength: rules.maxLength(64),
                password: rules.helpers.regex("password", /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[*.!@#$%^&(){}[:;<>,.?~_/+=|]).{8,64}$/),
            },
            plan_id: {required: rules.required},
            cf_turnstile_response: { required: rules.required }
        },
    },

    computed: {
        ...mapState('Plans', ['PLAN_LITE']),
        ...mapGetters('Plans', ['getSelectablePlansWhenRegistering']),
        ...mapState('Products', ['products']),
        passValidation() {
            return {
                uppercase: this.form.password ? /[A-Z]/.test(this.form.password) : false,
                lowercase: this.form.password ? /[a-z]/.test(this.form.password) : false,
                number: this.form.password ? /[0-9]/.test(this.form.password) : false,
                specialChar: this.form.password ? /[*.!@#$%^&(){}[:;<>,.?~_/+=|]/.test(this.form.password) : false,
                length: this.form.password ? /^.{8,}$/.test(this.form.password) : false,
            };
        },
    },
    methods: {
        ...mapActions("Auth", ["auth"]),
        ...mapActions("Roles", ["fetchRoles"]),

        onSubmit: function () {
            if (!this.form.validate()) return;

            this.$apiService
                .post("register", this.form)
                .then(response => {
                    this.$tokenService.saveToken(response.data.access_token);
                    this.$apiService.setBearer();
                    this.$apiService.mountInterceptor401();

                    this.$store.commit("Auth/SET_TOKEN", response.data.access_token);
                    this.$store.commit("User/SET_USER", response.data.user);
                    this.$store.commit("User/SET_ACTIVE_ACCOUNT");
                    this.$store.dispatch("Roles/fetchRoles");

					window.turnstile.remove(this.cf_turnstile_widget_id);

					this.$router.push({name: "verify-email"});
                })
                .catch(e => {
                    window.turnstile.reset(this.cf_turnstile_widget_id)
                })
        },
    },
};
</script>
